/**
 * "Ask by text" for CDL Consultants permission on one case (Lloyd, 14 Oct 2026: "Is there a way to
 * send them a message that allows us to get confirmation without having them check the portal?").
 *
 * Sits beside the consent chip. Staff read Lloyd's exact text in the dialog and press Send; the
 * client's Y or N is recorded as their sharing consent on this case by the inbound text webhook,
 * never as an answer to an offer. One case per press.
 *
 * Shown only on a CDL Consultants case. Where it cannot be sent (no phone, no text consent, a
 * shared number) the button is disabled and says why.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import ClientUpdateApi, { CdlConsentRequestStatus } from '../../api/ClientUpdateApi';

interface Props {
  Ticket_guid: string;
}

const CdlConsentRequestButton: React.FC<Props> = ({ Ticket_guid }) => {
  const [status, setStatus] = useState<CdlConsentRequestStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    ClientUpdateApi.cdlConsentRequest(Ticket_guid)
      .then(setStatus)
      // An older API has no such route; the case page still renders, without the button.
      .catch(() => setStatus(null));
  }, [Ticket_guid]);

  useEffect(load, [load]);

  if (!status?.applies) return null;

  const send = async (): Promise<void> => {
    setSending(true);
    setError(null);
    try {
      await ClientUpdateApi.sendCdlConsentRequest(Ticket_guid);
      setOpen(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The permission request could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const waitingSince = status.openRequestSentAt
    ? new Date(status.openRequestSentAt).toLocaleDateString('en-US')
    : null;

  return (
    <Box sx={{ mt: 1 }}>
      <Tooltip title={status.cannotSend ?? ''}>
        <span>
          <Button size="small" variant="outlined" disabled={Boolean(status.cannotSend)} onClick={() => setOpen(true)}>
            {waitingSince ? 'Ask CDL permission by text again' : 'Ask CDL permission by text'}
          </Button>
        </span>
      </Tooltip>
      {waitingSince && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Asked by text on {waitingSince}. Waiting for a Y or N.
        </Typography>
      )}

      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ask the client about CDL Consultants</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This text goes to the client by SMS and into their message thread. A reply of Y or Yes
            records permission on this case; N or No records a refusal. Anything else comes to you.
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            {status.text}
          </Typography>
          {waitingSince && (
            <Alert severity="info" sx={{ mt: 2 }}>
              A request sent on {waitingSince} is still waiting. Sending again replaces it.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={send} disabled={sending}>
            {sending ? 'Sending...' : 'Send text'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CdlConsentRequestButton;
